import { AlertTriangle, RotateCcw, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import { Link } from "react-router";

import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@runcheetah/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  formatImageQaBlockLabel,
  formatImageQaPassRate,
  formatImageQaRetryCount,
  formatImageQaScore,
  getImageQaFailurePolicyLabel,
  getImageQaStatusLabel,
  getImageQaTaskKindLabel,
} from "./image-qa-helpers";
import type {
  SocialPostImageQaPolicyBucket,
  SocialPostImageQaSummary,
  SocialPostImageQaSummaryEvent,
} from "./types";

type IconComponent = typeof ShieldCheck;

const formatCount = (value: number): string => value.toLocaleString();

const getStatusTone = (status: SocialPostImageQaSummaryEvent["selectionStatus"]): string => {
  switch (status) {
    case "qa_passed":
      return "border-emerald-200 bg-emerald-50 text-emerald-800";
    case "qa_failed_selected_best":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "qa_unavailable_selected_best":
      return "border-orange-200 bg-orange-50 text-orange-800";
    case "task_failed":
      return "border-red-200 bg-red-50 text-red-800";
    case "qa_disabled":
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
};

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  icon: IconComponent;
  tone?: "good" | "warn" | "danger" | "neutral";
}) {
  const toneClass =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : tone === "danger"
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-border bg-background text-muted-foreground";

  return (
    <div className="rounded-2xl border bg-background p-4 shadow-xs">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </div>
        <span className={`rounded-full border p-1.5 ${toneClass}`}>
          <Icon className="size-4" aria-hidden="true" />
        </span>
      </div>
      <div className="mt-3 font-mono text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{detail}</div>
    </div>
  );
}

function PolicyBucketCard({ bucket }: { bucket: SocialPostImageQaPolicyBucket }) {
  const fallbackCount = bucket.failedSelectedBestCount + bucket.unavailableSelectedBestCount;

  return (
    <article className="rounded-2xl border bg-background p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">
            {getImageQaFailurePolicyLabel(bucket.failurePolicy)}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatCount(bucket.evaluationCount)} evaluated image selection
            {bucket.evaluationCount === 1 ? "" : "s"}
          </p>
        </div>
        <Badge variant="outline" className="font-mono">
          {bucket.failurePolicy}
        </Badge>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <BucketStat label="Fallbacks" value={fallbackCount} />
        <BucketStat label="Retries" value={bucket.retryUsedCount} />
        <BucketStat label="Failed QA" value={bucket.failedSelectedBestCount} />
        <BucketStat label="Task failures" value={bucket.taskFailedCount} />
      </div>
    </article>
  );
}

function BucketStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border bg-muted/20 p-3">
      <div className="font-mono text-lg font-semibold tabular-nums">{formatCount(value)}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function RecentEventTaskCell({
  brandId,
  event,
}: {
  brandId: string;
  event: SocialPostImageQaSummaryEvent;
}) {
  const taskLabel = getImageQaTaskKindLabel(event.taskKind);
  const taskCode = event.taskId.slice(0, 8);

  return (
    <div className="min-w-40 space-y-1">
      <div className="text-sm font-medium">{taskLabel}</div>
      <div className="font-mono text-xs text-muted-foreground">
        {event.generationStatus} · {taskCode}
      </div>
      {event.campaignId ? (
        <Link
          to={`/brands/${brandId}/campaigns/${event.campaignId}`}
          className="inline-flex text-xs font-medium text-primary hover:underline"
        >
          Open campaign
        </Link>
      ) : null}
    </div>
  );
}

function RecentEventsTable({
  brandId,
  events,
  formatDate,
}: {
  brandId: string;
  events: SocialPostImageQaSummaryEvent[];
  formatDate: (value: string) => string;
}) {
  if (events.length === 0) {
    return (
      <div className="flex h-28 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No image QA attention events found for this brand.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border bg-background">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Updated</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Task</TableHead>
            <TableHead>Block</TableHead>
            <TableHead>Policy</TableHead>
            <TableHead>Retry / Score</TableHead>
            <TableHead>Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id} className="align-top">
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                {formatDate(event.updatedAt)}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={getStatusTone(event.selectionStatus)}>
                  {getImageQaStatusLabel(event.selectionStatus)}
                </Badge>
              </TableCell>
              <TableCell>
                <RecentEventTaskCell brandId={brandId} event={event} />
              </TableCell>
              <TableCell className="max-w-56 text-xs text-muted-foreground">
                <span title={formatImageQaBlockLabel(event)}>{formatImageQaBlockLabel(event)}</span>
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="font-mono">
                  {event.failurePolicy}
                </Badge>
              </TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">
                <div>{formatImageQaRetryCount(event.retryCount)}</div>
                <div>{formatImageQaScore(event.qaScorePct)}</div>
              </TableCell>
              <TableCell
                className="max-w-72 truncate text-xs text-muted-foreground"
                title={event.error ?? undefined}
              >
                {event.error ?? "--"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function ImageQaSummaryContent({
  brandId,
  summary,
  formatDate = formatDateTime,
}: {
  brandId: string;
  summary: SocialPostImageQaSummary;
  formatDate?: (value: string) => string;
}) {
  const fallbackCount =
    summary.totals.failedSelectedBestCount + summary.totals.unavailableSelectedBestCount;

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <ShieldCheck className="size-4 text-muted-foreground" aria-hidden="true" />
                Image QA
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Operational view of generated social post image selections, retries, and failure
                policy outcomes.
              </p>
            </div>
            <div className="rounded-2xl border bg-background px-3 py-2 text-right shadow-xs">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Inspected tasks
              </div>
              <div className="font-mono text-lg font-semibold tabular-nums">
                {formatCount(summary.inspectedTaskCount)}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Evaluations"
              value={formatCount(summary.totals.evaluationCount)}
              detail={`${formatImageQaPassRate(summary)} pass rate`}
              icon={ShieldCheck}
              tone="neutral"
            />
            <MetricCard
              label="Passed"
              value={formatCount(summary.totals.passedCount)}
              detail="Selected after passing QA"
              icon={ShieldCheck}
              tone="good"
            />
            <MetricCard
              label="Fallbacks"
              value={formatCount(fallbackCount)}
              detail="Selected best when QA did not pass"
              icon={ShieldAlert}
              tone="warn"
            />
            <MetricCard
              label="Retries used"
              value={formatCount(summary.totals.retryUsedCount)}
              detail="Image groups with retry attempts"
              icon={RotateCcw}
              tone="neutral"
            />
            <MetricCard
              label="Failed QA fallback"
              value={formatCount(summary.totals.failedSelectedBestCount)}
              detail="Best candidate selected after failed QA"
              icon={AlertTriangle}
              tone="warn"
            />
            <MetricCard
              label="QA unavailable"
              value={formatCount(summary.totals.unavailableSelectedBestCount)}
              detail="Best candidate selected without QA result"
              icon={ShieldQuestion}
              tone="warn"
            />
            <MetricCard
              label="QA disabled"
              value={formatCount(summary.totals.disabledCount)}
              detail="Selections made while QA was off"
              icon={ShieldQuestion}
              tone="neutral"
            />
            <MetricCard
              label="Task failures"
              value={formatCount(summary.totals.taskFailedCount)}
              detail="Tasks blocked by fail_task policy"
              icon={AlertTriangle}
              tone="danger"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <Card className="h-fit overflow-hidden">
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="text-sm font-semibold">Failure Policy Buckets</CardTitle>
            <p className="text-xs text-muted-foreground">
              Counts grouped by the policy captured on generated image candidate metadata.
            </p>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            {summary.byFailurePolicy.length > 0 ? (
              summary.byFailurePolicy.map((bucket) => (
                <PolicyBucketCard key={bucket.failurePolicy} bucket={bucket} />
              ))
            ) : (
              <div className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                No image QA policy metadata recorded yet.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="text-sm font-semibold">Recent Attention Events</CardTitle>
            <p className="text-xs text-muted-foreground">
              QA fallbacks and fail-task errors that need operator awareness.
            </p>
          </CardHeader>
          <CardContent className="p-4">
            <RecentEventsTable
              brandId={brandId}
              events={summary.recentEvents}
              formatDate={formatDate}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ImageQaSection({ brandId }: { brandId: string }) {
  const query = trpc.admin.brands.socialPostImageQaSummary.useQuery(
    { brandId },
    { refetchOnWindowFocus: true, staleTime: 0 },
  );

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64" />
        <div className="grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (query.error || !query.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Image QA</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-destructive">
          {query.error?.message ?? "Failed to load image QA summary."}
        </CardContent>
      </Card>
    );
  }

  return <ImageQaSummaryContent brandId={brandId} summary={query.data} />;
}
